import * as _ from 'lodash-es';
import { TopicPrivacy } from '../../graphql/generated/graphql';
import type { TaggedToastProps } from '../../toasts/toasts';
import { postToasts } from '../../toasts/toasts';

const PRIVACY_TYPE_MAPPER = {
  PRIVATE: 'restricted',
  SECRET: 'restricted',
  OPEN: 'open',
  PUBLIC: 'open',
};

const splitToOpenAndRestrictedSet = (
  privacies: TopicPrivacy[],
): [Set<TopicPrivacy>, Set<TopicPrivacy>] => {
  const { open = [], restricted = [] } = _.groupBy(
    privacies,
    (privacy) => PRIVACY_TYPE_MAPPER[privacy],
  );

  return [new Set(open), new Set(restricted)];
};

export const POST_TOPIC_TOAST_TAG = 'post-topic-toast';

type TopicShape = { id: string; name: string; privacy: TopicPrivacy };
interface GetPostTopicToastOptions {
  adding: boolean;
  addTopic: (topic: { id: string }) => unknown;
  organizationName: string;
  postTopics: TopicShape[];
  published: boolean;
  removeTopic: (topic: { id: string; name: string }) => unknown;
  topic: TopicShape;
}

const getPostTopicToast = ({
  adding,
  addTopic,
  organizationName,
  postTopics: oldTopics,
  published,
  removeTopic,
  topic: newTopic,
}: GetPostTopicToastOptions): TaggedToastProps | null => {
  if (!published) return null;

  const newTopics = adding
    ? [...oldTopics, newTopic]
    : oldTopics.filter(({ id }) => id !== newTopic.id);

  const [oldTopicsOpenSet, oldTopicsRestrictedSet] =
    splitToOpenAndRestrictedSet(oldTopics.map((topic) => topic.privacy));

  const [newTopicsOpenSet, newTopicsRestrictedSet] =
    splitToOpenAndRestrictedSet(newTopics.map((topic) => topic.privacy));

  const wasEmpty =
    oldTopicsOpenSet.size === 0 && oldTopicsRestrictedSet.size === 0;
  const isEmpty =
    newTopicsOpenSet.size === 0 && newTopicsRestrictedSet.size === 0;
  const wasOpen = wasEmpty || oldTopicsOpenSet.size > 0;
  const isOpen = isEmpty || newTopicsOpenSet.size > 0;
  const wasRestricted = !wasOpen;
  const isRestricted = !isOpen;
  const isSecret = newTopicsRestrictedSet.has(TopicPrivacy.Secret);

  const onUndo = () => {
    const undo = adding ? removeTopic : addTopic;
    undo(newTopic);
  };

  if (wasEmpty) {
    if (isRestricted) {
      if (isSecret) {
        return postToasts.postOnlyAccessibleByTopicMembersSecret({
          onUndo,
        });
      }
      return postToasts.postOnlyAccessibleByTopicMembersPrivate({
        onUndo,
      });
    }
    return null;
  }

  if (wasOpen) {
    if (isRestricted) {
      if (newTopicsRestrictedSet.size > 1) {
        return postToasts.postOnlyAccessibleByTopicMembersPrivateAndSecret({
          onUndo,
        });
      }

      if (isSecret) {
        return postToasts.postOnlyAccessibleByTopicMembersSecret({
          onUndo,
        });
      }

      return postToasts.postOnlyAccessibleByTopicMembersPrivate({
        onUndo,
      });
    }
    if (isOpen) {
      // Is still open but added some restricted topic
      if (newTopicsRestrictedSet.size > 0) {
        if (newTopicsRestrictedSet.size === 1) {
          if (isSecret) {
            return postToasts.postRemainsAccessibleByAnyoneOpenAndSecret({
              organizationName,
            });
          }

          return postToasts.postRemainsAccessibleByAnyoneOpenAndPrivate({
            organizationName,
          });
        }

        return postToasts.postRemainsAccessibleByAnyoneOpenSecretAndPrivate({
          organizationName,
        });
      }
    }
    return null;
  }

  if (wasRestricted) {
    if (isEmpty) {
      return postToasts.postNowAccessibleByAnyone({
        organizationName,
        onUndo,
      });
    }

    if (isOpen) {
      if (newTopicsRestrictedSet.size === 1) {
        if (isSecret) {
          return postToasts.postNowAccessibleByAnyoneOpenAndSecret({
            organizationName,
            onUndo,
          });
        }

        return postToasts.postNowAccessibleByAnyoneOpenAndPrivate({
          organizationName,
          onUndo,
        });
      }

      return postToasts.postNowAccessibleByAnyoneOpenSecretAndPrivate({
        organizationName,
        onUndo,
      });
    }

    if (isRestricted) {
      if (oldTopicsRestrictedSet.size === newTopicsRestrictedSet.size) {
        // Added already existed topic type
        return null;
      }

      if (newTopicsRestrictedSet.size > 1) {
        return postToasts.postRemainsAccessibleByTopicMembersPrivateAndSecret;
      }

      if (isSecret) {
        return postToasts.postRemainsAccessibleByTopicMembersSecret;
      }

      return postToasts.postRemainsAccessibleByTopicMembersPrivate;
    }
    return null;
  }
  return null;
};

export default getPostTopicToast;
